import { Briefcase, Clock, Download, FileText, Receipt } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import {
  BarChart,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ChartDateRangeFilter,
  ChartFilters,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui'
import { useToast } from '@/hooks'
import { useDateFilter } from '@/hooks/useDateFilter'
import type { DateRange } from '@/lib/utils/filterByDateRange'
import type { Account } from '@/modules/account/types'
import { hasProjectSucceeded } from '@/modules/projects/status'
import { chartColors, formatDateRange, numberToCompactCurrency } from '@/utils'
import { billableAmount, billableHours } from '../../billing'
import { MetricCard } from '../MetricCard'
import { billingColumns } from './columns'
import { CHART_HEIGHT, DAY_RANGES } from './constants'
import { TopCasesCard } from './TopCasesCard'
import type { Project } from './types'
import {
  billableByMonth,
  calculateProjectUsageMetrics,
  exportAccountSummaryPDF,
  exportBillingCSV,
  topCasesByAmount,
} from './utils'

interface ProjectUsageDashboardProps {
  projects: Project[]
  account?: Account | null
}

const TOP_CASES = 6

export function ProjectUsageDashboard({
  projects,
  account,
}: ProjectUsageDashboardProps) {
  const [dateRange, setDateRange] = useState<DateRange>('90d')
  const { successToast, errorToast } = useToast()

  const { initialDate, finalDate } = useMemo(() => {
    const dates = projects
      .filter((project) => project.createdAt)
      .map((project) => new Date(project.createdAt))
      .filter((date) => !Number.isNaN(date.getTime()))

    if (dates.length === 0) {
      return { initialDate: undefined, finalDate: undefined }
    }

    const timestamps = dates.map((date) => date.getTime())
    return {
      initialDate: new Date(Math.min(...timestamps))
        .toISOString()
        .split('T')[0],
      finalDate: new Date(Math.max(...timestamps)).toISOString().split('T')[0],
    }
  }, [projects])

  const filteredProjects = useDateFilter(projects, dateRange, {
    dateField: 'createdAt',
  })

  // Billing runs on processed cases only: a case is billable once its analysis is done.
  const billableProjects = filteredProjects.filter(hasProjectSucceeded)

  const metrics = useMemo(
    () => calculateProjectUsageMetrics(filteredProjects),
    [filteredProjects],
  )

  const billablePages = billableProjects.reduce(
    (sum, project) => sum + project.numberOfPages,
    0,
  )
  const totalHours = billableHours(billablePages)
  const totalAmount = billableAmount(billablePages)

  const topCases = useMemo(
    () => topCasesByAmount(billableProjects, TOP_CASES),
    [billableProjects],
  )

  const amountByMonth = useMemo(
    () =>
      billableByMonth(billableProjects).map((point) => ({
        ...point,
        color: chartColors.blue,
      })),
    [billableProjects],
  )

  const formatPeriod = useCallback(() => {
    if (typeof dateRange === 'object' && dateRange.from) {
      const to = dateRange.to ? dateRange.to : 'Present'
      return formatDateRange(dateRange.from, to)
    }
    if (dateRange === 'all') {
      return initialDate && finalDate
        ? formatDateRange(initialDate, finalDate)
        : 'All time'
    }
    const now = new Date()
    const days = DAY_RANGES[dateRange as string] ?? 365
    const start = new Date(now.getTime() - days * 86400000)
    return formatDateRange(start, now)
  }, [dateRange, initialDate, finalDate])

  const handleExportCSV = useCallback(() => {
    if (billableProjects.length === 0) {
      errorToast(
        'Nothing to export',
        'No processed cases in the selected period.',
      )
      return
    }
    exportBillingCSV(billableProjects)
    successToast(
      'Export ready',
      `Exported ${billableProjects.length} cases to CSV.`,
    )
  }, [billableProjects, errorToast, successToast])

  const handleExportPDF = useCallback(async () => {
    if (billableProjects.length === 0) {
      errorToast(
        'Nothing to export',
        'No processed cases in the selected period.',
      )
      return
    }
    try {
      await exportAccountSummaryPDF({
        accountName: account?.name ?? '',
        plan: account?.plan ?? '',
        periodLabel: formatPeriod(),
        billableCases: billableProjects.length,
        billablePages,
        billableHours: totalHours,
        billableAmount: totalAmount,
        accountCreatedAt: account?.createdAt ?? '',
        cases: topCasesByAmount(billableProjects, billableProjects.length),
      })
      successToast('Export ready', 'Billing statement exported to PDF.')
    } catch {
      errorToast('Export failed', 'Could not generate the PDF.')
    }
  }, [
    account,
    formatPeriod,
    billableProjects,
    billablePages,
    totalHours,
    totalAmount,
    errorToast,
    successToast,
  ])

  const processingCount = metrics.activeProjects

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <ChartFilters>
          <ChartDateRangeFilter
            value={dateRange}
            onValueChange={setDateRange}
            initialDate={initialDate}
            finalDate={finalDate}
            presets={[
              { label: 'Last 30 days', value: '30d' },
              { label: 'Last 60 days', value: '60d' },
              { label: 'Last 90 days', value: '90d' },
              { label: 'This year', value: '1y' },
              { label: 'All time', value: 'all' },
            ]}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                theme="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs font-medium"
              >
                <Download className="h-3.5 w-3.5" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportPDF}>
                Billing statement (PDF)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportCSV}>
                Billing lines (CSV)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </ChartFilters>
      </div>

      <div className="space-y-2.5">
        <div className="flex items-baseline gap-2">
          <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-blue-5">
            Period
          </span>
          <span className="text-sm font-medium tabular-nums text-blue-7">
            {formatPeriod()}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="Cases"
            value={metrics.totalProjects}
            icon={Briefcase}
            subtitle={`${metrics.completedProjects} billable${processingCount > 0 ? ` · ${processingCount} processing` : ''}`}
          />
          <MetricCard
            label="Pages Analyzed"
            value={billablePages}
            icon={FileText}
            useLocale
            subtitle="Across billable cases"
          />
          <MetricCard
            label="Billable Hours"
            value={Math.round(totalHours)}
            icon={Clock}
            suffix=" hrs"
            subtitle="@ 5 min/page"
          />
          <MetricCard
            label="Billable Amount"
            value={totalAmount}
            icon={Receipt}
            prefix="$"
            useLocale
            subtitle="@ $200/hr"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Billable amount by month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-75">
              {amountByMonth.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  No processed cases in this period.
                </div>
              ) : (
                <BarChart
                  data={amountByMonth}
                  height={CHART_HEIGHT}
                  rotateXLabels={amountByMonth.length > 6}
                  color={chartColors.blue}
                  valueFormat="currency"
                  yTickFormatter={(value: number) =>
                    numberToCompactCurrency(value)
                  }
                />
              )}
            </div>
          </CardContent>
        </Card>

        <TopCasesCard cases={topCases} />
      </div>

      <div className="overflow-hidden rounded-lg border">
        <DataTable
          columns={billingColumns as any}
          data={billableProjects}
          height="h-auto"
          initialPageSize={10}
          pageSizes={[10, 25, 50, 100]}
          scrollbarClassName="overflow-auto scrollbar-thin scrollbar-thumb-neutral-300 hover:scrollbar-thumb-neutral-400 scrollbar-track-transparent"
          emptyMessage="No processed cases in the selected period."
        />
      </div>
    </div>
  )
}
